import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { P } from '@massar/shared/permissions';
import { AppShell } from '@/components/layout/AppShell';
import { RequireAuth, RequirePermission } from './guards';
import { RouteErrorElement } from './RouteErrorBoundary';
import { NotFoundPage } from '@/features/misc/NotFoundPage';
import LoginPage from '@/features/auth/pages/LoginPage';

/**
 * Route tree with lazy route groups (docs/01 §1).
 *
 * Each feature area is a separate chunk, so signing in downloads the dashboard and the
 * chrome — not the journal editor a treasurer will never open. `AppShell` provides the
 * `Suspense` boundary these `lazy()` calls need.
 */
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));

const SafesPage = lazy(() => import('@/features/treasury/pages/SafesPage'));
const SafeStatementPage = lazy(() => import('@/features/treasury/pages/SafeStatementPage'));
const ReconciliationPage = lazy(() => import('@/features/treasury/pages/ReconciliationPage'));
const SafeReportPage = lazy(() => import('@/features/treasury/pages/SafeReportPage'));
const EmployeesPage = lazy(() => import('@/features/payroll/pages/EmployeesPage'));
const PayrollListPage = lazy(() => import('@/features/payroll/pages/PayrollListPage'));
const PayrollDetailPage = lazy(() => import('@/features/payroll/pages/PayrollDetailPage'));
const PayslipPage = lazy(() => import('@/features/payroll/pages/PayslipPage'));
const EmployeeBookPage = lazy(() => import('@/features/payroll/pages/EmployeeBookPage'));
const EmployeeReportListPage = lazy(() => import('@/features/payroll/pages/EmployeeReportListPage'));
const EmployeeReportDetailPage = lazy(() => import('@/features/payroll/pages/EmployeeReportDetailPage'));
const EmployeeInvoiceFormPage = lazy(() => import('@/features/payroll/pages/EmployeeInvoiceFormPage'));

const PropertiesListPage = lazy(() => import('@/features/properties/pages/PropertiesListPage'));
/** One site's report — what has been spent on it (docs/10 §4.1a). */
const PropertyStatementPage = lazy(() => import('@/features/properties/pages/PropertyStatementPage'));
const SaleContractsPage = lazy(() => import('@/features/properties/pages/SaleContractsPage'));
const LeasesPage = lazy(() => import('@/features/properties/pages/LeasesPage'));
/** Real Estate's own trading register (docs/10 §4.2a) — not Construction's property list. */
const PropertyTreatsPage = lazy(() => import('@/features/properties/pages/PropertyTreatsPage'));
/** Real Estate's rent agreements and their schedules (docs/10 §4.2b). */
const TenanciesPage = lazy(() => import('@/features/properties/pages/TenanciesPage'));
/** Real Estate's own invoice form (docs/10 §4.2d) — six questions, not fifteen. */
const RealEstateInvoiceFormPage = lazy(() => import('@/features/realestate/pages/RealEstateInvoiceFormPage'));
/** Construction's owners and their money (docs/10 §4.1a). */
const OwnersPage = lazy(() => import('@/features/properties/pages/OwnersPage'));
const OwnerStatementPage = lazy(() => import('@/features/properties/pages/OwnerStatementPage'));

const GeneralReportPage = lazy(() => import('@/features/reports/pages/GeneralReportPage'));
const ActivityReportPage = lazy(() => import('@/features/reports/pages/ActivityReportPage'));
const SectionReportPage = lazy(() => import('@/features/reports/pages/SectionReportPage'));
const ExpensesReportPage = lazy(() => import('@/features/reports/pages/ExpensesReportPage'));
const CustomersPage = lazy(() => import('@/features/customers/pages/CustomersPage'));
const AgencyCustomersPage = lazy(() => import('@/features/agency/pages/AgencyCustomersPage'));
const AgencyInvoiceListPage = lazy(() => import('@/features/agency/pages/AgencyInvoiceListPage'));
const AgencyInvoiceFormPage = lazy(() => import('@/features/agency/pages/AgencyInvoiceFormPage'));
const CustomerStatementPage = lazy(() => import('@/features/customers/pages/CustomerStatementPage'));
/** The Cleaner section's orders and the expenses spent on them (docs/10 §50). */
const CleaningOrdersPage = lazy(() => import('@/features/cleaner/pages/CleaningOrdersPage'));
/** One order: what it brought in, what it cost, and the customer behind it (docs/10 §57). */
const CleaningOrderPage = lazy(() => import('@/features/cleaner/pages/CleaningOrderPage'));
const CleanerExpensesPage = lazy(() => import('@/features/cleaner/pages/CleanerExpensesPage'));
const CleanerCustomersPage = lazy(() => import('@/features/cleaner/pages/CleanerCustomersPage'));
const CleanerCustomerPage = lazy(() => import('@/features/cleaner/pages/CleanerCustomerPage'));
const CleanerReportPage = lazy(() => import('@/features/cleaner/pages/CleanerReportPage'));
/** Real Estate's rent-payers (docs/10 §4.2b) — the same register on the `tenant` role. */
const TenantsPage = lazy(() => import('@/features/customers/pages/TenantsPage'));

const AccountsPage = lazy(() => import('@/features/accounting/pages/AccountsPage'));
const JournalListPage = lazy(() => import('@/features/accounting/pages/JournalListPage'));
const JournalFormPage = lazy(() => import('@/features/accounting/pages/JournalFormPage'));
const JournalDetailPage = lazy(() => import('@/features/accounting/pages/JournalDetailPage'));
const PeriodsPage = lazy(() => import('@/features/accounting/pages/PeriodsPage'));

const PartiesPage = lazy(() => import('@/features/parties/pages/PartiesPage'));

const InvoiceListPage = lazy(() => import('@/features/invoices/pages/InvoiceListPage'));
const InvoiceFormPage = lazy(() => import('@/features/invoices/pages/InvoiceFormPage'));
const InvoiceDetailPage = lazy(() => import('@/features/invoices/pages/InvoiceDetailPage'));

const ExpenseListPage = lazy(() => import('@/features/expenses/pages/ExpenseListPage'));
const ExpenseFormPage = lazy(() => import('@/features/expenses/pages/ExpenseFormPage'));
const ExpenseDetailPage = lazy(() => import('@/features/expenses/pages/ExpenseDetailPage'));

const PaymentListPage = lazy(() => import('@/features/payments/pages/PaymentListPage'));
const PaymentFormPage = lazy(() => import('@/features/payments/pages/PaymentFormPage'));
const PaymentDetailPage = lazy(() => import('@/features/payments/pages/PaymentDetailPage'));

const CommissionListPage = lazy(() => import('@/features/commissions/pages/CommissionListPage'));
const CommissionDetailPage = lazy(() => import('@/features/commissions/pages/CommissionDetailPage'));
const CommissionStatementPage = lazy(() => import('@/features/commissions/pages/CommissionStatementPage'));
const CommissionPlanListPage = lazy(() => import('@/features/commissions/pages/CommissionPlanListPage'));
const CommissionPlanFormPage = lazy(() => import('@/features/commissions/pages/CommissionPlanFormPage'));
const CommissionPlanDetailPage = lazy(() => import('@/features/commissions/pages/CommissionPlanDetailPage'));

const DealListPage = lazy(() => import('@/features/brokers/pages/DealListPage'));
const DealFormPage = lazy(() => import('@/features/brokers/pages/DealFormPage'));
const DealDetailPage = lazy(() => import('@/features/brokers/pages/DealDetailPage'));
const BrokerProfilesPage = lazy(() => import('@/features/brokers/pages/BrokerProfilesPage'));
const PayoutListPage = lazy(() => import('@/features/brokers/pages/PayoutListPage'));
const TreatsPage = lazy(() => import('@/features/brokers/pages/TreatsPage'));
const BrokerStatementPage = lazy(() => import('@/features/brokers/pages/BrokerStatementPage'));

const TrialBalancePage = lazy(() => import('@/features/reports/pages/TrialBalancePage'));
const IncomeStatementPage = lazy(() => import('@/features/reports/pages/IncomeStatementPage'));
const BalanceSheetPage = lazy(() => import('@/features/reports/pages/BalanceSheetPage'));
const CashFlowPage = lazy(() => import('@/features/reports/pages/CashFlowPage'));
const GeneralLedgerPage = lazy(() => import('@/features/reports/pages/GeneralLedgerPage'));
const AccountStatementPage = lazy(() => import('@/features/reports/pages/AccountStatementPage'));
const AgingPage = lazy(() => import('@/features/reports/pages/AgingPage'));
const ExpenseByCategoryPage = lazy(() => import('@/features/reports/pages/ExpenseByCategoryPage'));
const InvoiceRegisterPage = lazy(() => import('@/features/reports/pages/InvoiceRegisterPage'));
const TreatReportPage = lazy(() => import('@/features/reports/pages/TreatReportPage'));
const DealProfitabilityPage = lazy(() => import('@/features/reports/pages/DealProfitabilityPage'));

const UserListPage = lazy(() => import('@/features/users/pages/UserListPage'));
const UserFormPage = lazy(() => import('@/features/users/pages/UserFormPage'));
const UserDetailPage = lazy(() => import('@/features/users/pages/UserDetailPage'));
const RoleListPage = lazy(() => import('@/features/rbac/pages/RoleListPage'));
const RoleFormPage = lazy(() => import('@/features/rbac/pages/RoleFormPage'));
const RoleDetailPage = lazy(() => import('@/features/rbac/pages/RoleDetailPage'));
const SectionsPage = lazy(() => import('@/features/settings/pages/SectionsPage'));
const CurrenciesPage = lazy(() => import('@/features/settings/pages/CurrenciesPage'));
const MyAccountPage = lazy(() => import('@/features/account/pages/MyAccountPage'));

const AuditPage = lazy(() => import('@/features/audit/pages/AuditPage'));

export const routes = [
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorElement />,
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorElement />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardPage /> },

          {
            path: 'treasury',
            children: [
              { index: true, element: <Navigate to="safes" replace /> },
              {
                element: <RequirePermission permission={P.SAFE_VIEW} />,
                children: [{ path: 'safes', element: <SafesPage /> }],
              },
              {
                element: <RequirePermission permission={P.REPORT_VIEW} />,
                children: [{ path: 'statement', element: <SafeStatementPage /> }],
              },
              {
                element: <RequirePermission permission={P.SAFE_RECON_VIEW} />,
                children: [{ path: 'reconciliation', element: <ReconciliationPage /> }],
              },
              {
                // The Safe report is gated globally — holding the permission in one
                // section must not open every section's cash movement (docs/03 §2).
                element: <RequirePermission permission={P.REPORT_CONSOLIDATED_VIEW} global />,
                children: [{ path: 'safe-report', element: <SafeReportPage /> }],
              },
            ],
          },

          {
            path: 'accounting',
            children: [
              { index: true, element: <Navigate to="journal" replace /> },
              {
                element: <RequirePermission permission={P.ACCOUNT_VIEW} />,
                children: [{ path: 'accounts', element: <AccountsPage /> }],
              },
              {
                element: <RequirePermission permission={P.JOURNAL_VIEW} />,
                children: [
                  { path: 'journal', element: <JournalListPage /> },
                  { path: 'journal/:id', element: <JournalDetailPage /> },
                ],
              },
              {
                element: <RequirePermission permission={P.JOURNAL_CREATE} />,
                children: [{ path: 'journal/new', element: <JournalFormPage /> }],
              },
              {
                element: <RequirePermission permission={P.PERIOD_VIEW} />,
                children: [{ path: 'periods', element: <PeriodsPage /> }],
              },
            ],
          },

          {
            // Parties and documents (Phase 5). `new` sits before `:id` so the literal
            // path is not swallowed by the parameter route.
            path: 'parties',
            element: <RequirePermission permission={P.PARTY_VIEW} />,
            children: [{ index: true, element: <PartiesPage /> }],
          },

          {
            path: 'invoices',
            children: [
              {
                element: <RequirePermission permission={P.INVOICE_CREATE} />,
                children: [{ path: 'new', element: <InvoiceFormPage /> }],
              },
              {
                /*
                 * Editing an invoice that has not reached the ledger. Declared before
                 * `:id` for the usual reason — the parameter route would otherwise
                 * swallow `edit` — and guarded on `invoice.update`, which is a separate
                 * grant from creating one. The API re-checks both the permission and the
                 * status; this is the convenience layer (docs/03 §4).
                 */
                element: <RequirePermission permission={P.INVOICE_UPDATE} />,
                children: [{ path: ':id/edit', element: <InvoiceFormPage /> }],
              },
              {
                element: <RequirePermission permission={P.INVOICE_VIEW} />,
                children: [
                  { index: true, element: <InvoiceListPage /> },
                  { path: ':id', element: <InvoiceDetailPage /> },
                ],
              },
            ],
          },

          {
            path: 'payments',
            children: [
              {
                element: <RequirePermission permission={P.PAYMENT_CREATE} />,
                children: [{ path: 'new', element: <PaymentFormPage /> }],
              },
              {
                element: <RequirePermission permission={P.PAYMENT_VIEW} />,
                children: [
                  { index: true, element: <PaymentListPage /> },
                  { path: ':id', element: <PaymentDetailPage /> },
                ],
              },
            ],
          },

          {
            path: 'expenses',
            children: [
              {
                element: <RequirePermission permission={P.EXPENSE_CREATE} />,
                children: [{ path: 'new', element: <ExpenseFormPage /> }],
              },
              {
                element: <RequirePermission permission={P.EXPENSE_VIEW} />,
                children: [
                  { index: true, element: <ExpenseListPage /> },
                  { path: ':id', element: <ExpenseDetailPage /> },
                ],
              },
            ],
          },

          {
            /*
             * Commissions (Phase 7). The literal `plans` and `statement` segments are
             * declared before `:id`, or the parameter route swallows them.
             */
            path: 'commissions',
            children: [
              {
                path: 'plans',
                children: [
                  {
                    element: <RequirePermission permission={P.COMMISSION_PLAN_CREATE} />,
                    children: [
                      { path: 'new', element: <CommissionPlanFormPage /> },
                      // Superseding is authoring a plan, so it shares the create grant.
                      { path: ':id/new-version', element: <CommissionPlanFormPage /> },
                    ],
                  },
                  {
                    element: <RequirePermission permission={P.COMMISSION_PLAN_VIEW} />,
                    children: [
                      { index: true, element: <CommissionPlanListPage /> },
                      { path: ':id', element: <CommissionPlanDetailPage /> },
                    ],
                  },
                ],
              },
              {
                element: <RequirePermission permission={P.COMMISSION_VIEW} />,
                children: [
                  { index: true, element: <CommissionListPage /> },
                  { path: 'statement', element: <CommissionStatementPage /> },
                  { path: ':id', element: <CommissionDetailPage /> },
                ],
              },
            ],
          },

          {
            path: 'brokers',
            children: [
              { index: true, element: <Navigate to="profiles" replace /> },
              {
                element: <RequirePermission permission={P.BR_PROFILE_VIEW} />,
                children: [{ path: 'profiles', element: <BrokerProfilesPage /> }],
              },
              {
                element: <RequirePermission permission={P.BR_PAYOUT_MANAGE} />,
                children: [{ path: 'payouts', element: <PayoutListPage /> }],
              },
              {
                /*
                 * Treats (docs/10 §3.2). Reading is `deal.view` — a broker watches his own
                 * positions; buying and selling ask for `capital.manage` inside the screen,
                 * because each spends or returns somebody else's money.
                 */
                element: <RequirePermission permission={P.BR_DEAL_VIEW} />,
                children: [{ path: 'treats', element: <TreatsPage /> }],
              },
              {
                /*
                 * The Broker Report. Two routes, one screen: reached from the register it
                 * opens on one broker, reached from the sidebar it asks which.
                 */
                element: <RequirePermission permission={P.BR_CAPITAL_VIEW} />,
                children: [
                  { path: 'report', element: <BrokerStatementPage /> },
                  { path: 'report/:id', element: <BrokerStatementPage /> },
                ],
              },
              {
                path: 'deals',
                children: [
                  {
                    element: <RequirePermission permission={P.BR_DEAL_MANAGE} />,
                    children: [{ path: 'new', element: <DealFormPage /> }],
                  },
                  {
                    element: <RequirePermission permission={P.BR_DEAL_VIEW} />,
                    children: [
                      { index: true, element: <DealListPage /> },
                      { path: ':id', element: <DealDetailPage /> },
                    ],
                  },
                ],
              },
            ],
          },

          {
            // Employees & payroll (docs/10 §5.1) — the engine has been complete since
            // Phase 9 and had no screens at all until now.
            path: 'payroll',
            children: [
              {
                element: <RequirePermission permission={P.PARTY_VIEW} />,
                children: [{ path: 'employees', element: <EmployeesPage /> }],
              },
              {
                /* Billing a member of staff (docs/10 §27). `invoice.create`, not
                   `party.view` — reading the register and raising a document against
                   somebody in it are different grants. */
                element: <RequirePermission permission={P.INVOICE_CREATE} />,
                children: [{ path: 'employees/invoice', element: <EmployeeInvoiceFormPage /> }],
              },
              {
                element: <RequirePermission permission={P.PAYROLL_VIEW} />,
                children: [
                  { index: true, element: <PayrollListPage /> },
                  /*
                   * **`summary` is now a view of `/payroll`, not a page of its own.**
                   *
                   * The two screens were merged (see `PayrollListPage`) because the menu
                   * offered both under the word "Payroll" and nothing on either said which
                   * was which. The redirect stays because the URL is in people's history and
                   * on the old Reports links, and a 404 is a worse answer than the screen
                   * they were looking for.
                   *
                   * Still declared **before** `:id`, or the parameter swallows it and
                   * "summary" is looked up as a payroll run id — the same ordering the
                   * broker and customer statements need for their literal segments.
                   */
                  { path: 'summary', element: <Navigate to="/payroll" replace /> },
                  { path: ':id', element: <PayrollDetailPage /> },
                  { path: ':id/payslips/:employeeId', element: <PayslipPage /> },
                ],
              },
              {
                /* The Employee Report states what people are paid *and* reports the documents
                   they are named on, so it carries both grants — the route mirrors the API. */
                element: <RequirePermission permission={P.PAYROLL_VIEW} />,
                children: [
                  {
                    element: <RequirePermission permission={P.INVOICE_VIEW} />,
                    children: [
                      /*
                       * **The book, not the comparison grid.**
                       *
                       * `/payroll/report` was eleven columns of mostly zeros — correct under
                       * I13, read in a currency the staff are not paid in, and useless. It is
                       * now a document: one page per person in their own currency.
                       *
                       * The grid still exists at `report/compare` because comparing people is
                       * a real question for a group that pays commission, and deleting a
                       * working screen to answer a different question would be trading one
                       * gap for another.
                       */
                      { path: 'report', element: <EmployeeBookPage /> },
                      { path: 'report/compare', element: <EmployeeReportListPage /> },
                      /*
                       * One person's own report. Declared **after** the literal `report`, the
                       * same ordering `summary` needs above — a parameter segment placed
                       * first swallows the word and looks up "report" as a party id.
                       */
                      { path: 'report/:partyId', element: <EmployeeReportDetailPage /> },
                      /*
                       * The screen was called Employee KPIs until it grew the drill-down that
                       * made it a report. The URL is in people's history and on links already
                       * sent, and a 404 is a worse answer than the screen they were after.
                       */
                      { path: 'kpi', element: <Navigate to="/payroll/report" replace /> },
                    ],
                  },
                ],
              },
            ],
          },

          {
            /*
             * Properties — Construction's register (docs/10 §4.1). The vertical has been
             * complete and tested since Phase 10 and had no screens; `requireModule` on the
             * server is what keeps it out of the sections that do not run it, so these
             * routes guard on the permission alone and let the API refuse the rest.
             */
            path: 'properties',
            children: [
              {
                element: <RequirePermission permission={P.PROP_PROPERTY_VIEW} />,
                children: [{ index: true, element: <PropertiesListPage /> }],
              },
              {
                /* The site report is gated on the owner grant, not the property one: its rows
                   are movements of an owner's money. */
                element: <RequirePermission permission={P.PROP_OWNER_VIEW} />,
                children: [{ path: ':id', element: <PropertyStatementPage /> }],
              },
              {
                element: <RequirePermission permission={P.PROP_CONTRACT_VIEW} />,
                children: [
                  { path: 'sale-contracts', element: <SaleContractsPage /> },
                  { path: 'leases', element: <LeasesPage /> },
                ],
              },
            ],
          },

          {
            /*
             * **Property owners** (docs/10 §4.1a) — who Construction builds for, and what is
             * left of the money each of them paid in.
             *
             * The same two-route shape the customer and broker statements use: a sidebar
             * link cannot carry an id, so `statement` asks which owner and `:id` answers.
             * The literal is declared before the parameter so it is not swallowed by it.
             */
            path: 'owners',
            element: <RequirePermission permission={P.PROP_OWNER_VIEW} />,
            children: [
              { index: true, element: <OwnersPage /> },
              { path: 'statement', element: <OwnerStatementPage /> },
              { path: ':id', element: <OwnerStatementPage /> },
            ],
          },

          {
            /*
             * **Real Estate treats** (docs/10 §4.2a) — the company buying a property with
             * its own cash and selling it on.
             *
             * A path of its own rather than a child of `/properties`, because it is not
             * that register: §4.1 moved the property list to Construction, and nesting the
             * two would suggest a treat points at a `Property` row. `requireModule` on the
             * server keeps it out of the sections that do not run it, so the route guards
             * on the permission alone.
             */
            path: 'treats',
            element: <RequirePermission permission={P.RE_TREAT_VIEW} />,
            children: [{ index: true, element: <PropertyTreatsPage /> }],
          },

          {
            /*
             * **Tenancies** (docs/10 §4.2c) — the rent agreements behind the Tenants
             * register: the term, the rhythm, the schedule, and the button that turns one
             * period into an invoice.
             *
             * Its own path rather than a child of `/tenants`, because a tenancy is a
             * document and that is a balance register. Gated on the tenancy grant, which the
             * section accountant holds in both halves — writing one moves no cash.
             */
            path: 'tenancies',
            element: <RequirePermission permission={P.RE_TENANCY_VIEW} />,
            children: [{ index: true, element: <TenanciesPage /> }],
          },

          {
            /*
             * **Real Estate's own invoice form** (docs/10 §4.2d).
             *
             * The register stays at `/invoices` — it is the shared list, and it already
             * filters by section and by kind. Only the *form* is the section's own, the way
             * Agency's is: the shared one asks nine questions Real Estate answers on no
             * document it raises.
             */
            path: 'realestate/invoices/new',
            element: <RequirePermission permission={P.INVOICE_CREATE} />,
            children: [{ index: true, element: <RealEstateInvoiceFormPage /> }],
          },

          {
            /*
             * **Tenants** (docs/10 §4.2b) — Real Estate's rent-payers and what they owe.
             *
             * `:id` renders the *same* statement component the customer register uses: one
             * party, one currency, a running balance built from income invoices and
             * receipts, which is the same document whichever register you arrived from.
             * Only the path differs, so the URL matches the list behind it.
             */
            path: 'tenants',
            element: <RequirePermission permission={P.PARTY_VIEW} />,
            children: [
              { index: true, element: <TenantsPage /> },
              { path: ':id', element: <CustomerStatementPage /> },
            ],
          },

          {
            /*
             * **Agency's own screens** (docs/10 §4.3).
             *
             * Its documents are money moving between the section and one customer, on
             * account — four questions, not eleven — so the register and the form here are
             * Agency's rather than the shared invoice screens narrowed by a filter. They
             * still write ordinary `Invoice` records through the same service; what is
             * section-specific is which questions get asked and which columns are worth a
             * table.
             *
             * `new` is declared before nothing else in this branch, but it stays a literal
             * segment under `invoices` for the same reason `/customers/statement` does: a
             * route that could be read as an id is a route that will be.
             */
            path: 'agency',
            children: [
              {
                path: 'customers',
                element: <RequirePermission permission={P.PARTY_VIEW} />,
                children: [{ index: true, element: <AgencyCustomersPage /> }],
              },
              {
                path: 'invoices',
                element: <RequirePermission permission={P.INVOICE_VIEW} />,
                children: [
                  { index: true, element: <AgencyInvoiceListPage /> },
                  {
                    path: 'new',
                    element: <RequirePermission permission={P.INVOICE_CREATE} />,
                    children: [{ index: true, element: <AgencyInvoiceFormPage /> }],
                  },
                ],
              },
            ],
          },

          {
            /*
             * **The Cleaner section** (docs/10 §50) — one register. An order's expenses are
             * entered from its row rather than on a screen of their own, because an expense
             * here always belongs to a job.
             */
            path: 'cleaner',
            children: [
              {
                path: 'orders',
                element: <RequirePermission permission={P.CL_ORDER_VIEW} />,
                children: [
                  { index: true, element: <CleaningOrdersPage /> },
                  { path: ':id', element: <CleaningOrderPage /> },
                ],
              },
              /* §52 — the section's expenses, its customers, and its own report. */
              {
                path: 'expenses',
                element: <RequirePermission permission={P.CL_ORDER_VIEW} />,
                children: [{ index: true, element: <CleanerExpensesPage /> }],
              },
              {
                path: 'customers',
                element: <RequirePermission permission={P.CL_ORDER_VIEW} />,
                children: [
                  { index: true, element: <CleanerCustomersPage /> },
                  { path: ':partyId', element: <CleanerCustomerPage /> },
                ],
              },
              {
                path: 'report',
                element: <RequirePermission permission={P.CL_ORDER_VIEW} />,
                children: [{ index: true, element: <CleanerReportPage /> }],
              },
            ],
          },

          {
            /*
             * Customers (docs/10 §5.3) — shared by Construction, Agency and Real Estate.
             * Read-only: a customer is a Party, created and edited on the party screens.
             * `invoice.view` is checked by the API too; the route guard is the cheap half.
             */
            path: 'customers',
            element: <RequirePermission permission={P.PARTY_VIEW} />,
            children: [
              { index: true, element: <CustomersPage /> },
              /*
               * The Customer Report as a *menu item* (docs/10 §1). Same two-route shape as
               * the Broker Report: a sidebar link cannot carry an id, so `statement` asks
               * which customer and `:id` answers — one component, two ways in. The literal
               * segment is declared before the parameter so it is not swallowed by it.
               */
              { path: 'statement', element: <CustomerStatementPage /> },
              { path: ':id', element: <CustomerStatementPage /> },
            ],
          },

          {
            path: 'reports',
            children: [
              /*
               * **The activity register is what `/reports` means now** (docs/10 §16). It
               * used to land on the trial balance, which is the right first page for an
               * accountant and the wrong one for everybody else — the register answers
               * "what has been entered", which is the question the menu is opened for.
               */
              { index: true, element: <Navigate to="activity" replace /> },
              {
                element: <RequirePermission permission={P.REPORT_VIEW} />,
                children: [
                  /*
                   * **The General Report** — everything created, across every section.
                   * `report.consolidated.view` is checked by the API for a caller whose
                   * scope actually spans sections; a section accountant opening this gets
                   * their own section's register, which is not a consolidation.
                   */
                  { path: 'activity', element: <ActivityReportPage /> },
                  /* One section's register. The slug survives a reseed; an id does not. */
                  { path: 'section/:slug', element: <SectionReportPage /> },
                  { path: 'trial-balance', element: <TrialBalancePage /> },
                  { path: 'aging', element: <AgingPage /> },
                  { path: 'expense-by-category', element: <ExpenseByCategoryPage /> },
                  {
                    /*
                     * **The expenses register** — the same component, fixed to one type, so
                     * "what does a row look like" is answered in exactly one place. The
                     * by-category summary above stays: they are the two halves of the
                     * question, *how much on what* and *which documents*.
                     */
                    path: 'expenses',
                    element: <ExpensesReportPage />,
                  },
                  /* Every invoice, both directions — the register a section downloads. */
                  { path: 'invoices', element: <InvoiceRegisterPage /> },
                  /*
                   * The financial statements (Phase 12). All five sit behind the plain
                   * `report.view` grant: the consolidated-vs-section question is decided
                   * by the service per request, because one URL serves both and a route
                   * guard would have to choose between denying a section accountant their
                   * own P&L and handing every one of them the group's.
                   */
                  /*
                   * The General Report sits with the statements because that is what it is
                   * — their consolidation. `report.consolidated.view` is checked by the API
                   * on top of the `report.view` this branch already requires: the route
                   * guard is the cheap half, and the expensive half is the one that counts.
                   */
                  { path: 'general', element: <GeneralReportPage /> },
                  { path: 'income-statement', element: <IncomeStatementPage /> },
                  { path: 'balance-sheet', element: <BalanceSheetPage /> },
                  { path: 'cash-flow', element: <CashFlowPage /> },
                  { path: 'general-ledger', element: <GeneralLedgerPage /> },
                  { path: 'account-statement', element: <AccountStatementPage /> },
                ],
              },
              /*
               * The treats register report. Gated on the treats grant rather than
               * `report.view`: its rows *are* those documents, and somebody barred from
               * the register should not reach them through a report — the same pairing
               * the invoice register makes with `invoice.view`.
               */
              {
                element: <RequirePermission permission={P.RE_TREAT_VIEW} />,
                children: [{ path: 'treats', element: <TreatReportPage /> }],
              },
              /*
               * Deal margin is gated on its own grant rather than the generic
               * `report.view` — `view_margin` guards it in the service too.
               */
              {
                element: <RequirePermission permission={P.BR_DEAL_VIEW_MARGIN} />,
                children: [{ path: 'deal-profitability', element: <DealProfitabilityPage /> }],
              },
            ],
          },

          {
            /*
             * Every account has this one, whatever their role — it is where a user reads
             * *their own* access, changes their password and picks their language.
             * No permission guard: it is about the caller, not about anybody else.
             */
            path: 'account',
            element: <MyAccountPage />,
          },

          {
            /*
             * Administration (Phase 11). Everything here is gated **globally**: users,
             * roles and sections are group-wide objects, so holding `user.view` inside
             * one section must not open the directory (docs/03 §4, the can/canGlobal
             * distinction). `new` sits before `:id` or the parameter route swallows it.
             */
            path: 'settings',
            children: [
              {
                element: <RequirePermission permission={P.USER_CREATE} global />,
                children: [{ path: 'users/new', element: <UserFormPage /> }],
              },
              {
                element: <RequirePermission permission={P.USER_VIEW} global />,
                children: [
                  { path: 'users', element: <UserListPage /> },
                  { path: 'users/:id', element: <UserDetailPage /> },
                ],
              },
              {
                element: <RequirePermission permission={P.ROLE_CREATE} global />,
                children: [{ path: 'roles/new', element: <RoleFormPage /> }],
              },
              {
                element: <RequirePermission permission={P.ROLE_UPDATE} global />,
                children: [{ path: 'roles/:id/edit', element: <RoleFormPage /> }],
              },
              {
                element: <RequirePermission permission={P.ROLE_VIEW} global />,
                children: [
                  { path: 'roles', element: <RoleListPage /> },
                  { path: 'roles/:id', element: <RoleDetailPage /> },
                ],
              },
              {
                element: <RequirePermission permission={P.SECTION_VIEW} global />,
                children: [{ path: 'sections', element: <SectionsPage /> }],
              },
              {
                /*
                 * Reading the currency list is a floor grant every money-touching role
                 * holds, so the *screen* is guarded on it rather than on `currency.manage`
                 * — an accountant checking what the dinar is booked at should not need
                 * permission to change it. The page hides its own write controls.
                 */
                element: <RequirePermission permission={P.CURRENCY_VIEW} />,
                children: [{ path: 'currencies', element: <CurrenciesPage /> }],
              },
            ],
          },

          {
            path: 'audit',
            element: <RequirePermission permission={P.AUDIT_VIEW} global />,
            children: [{ index: true, element: <AuditPage /> }],
          },

          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export const createRouter = () => createBrowserRouter(routes);

export default createRouter;
